import {
  FLOW_TRIGGERS,
  ORDER_TRIGGERS,
  type FlowCondition,
  type FlowGraph,
  type FlowNode,
  type FlowTrigger,
} from '@cartly/shared';
import { Alert, Button, Checkbox, SelectField, TextAreaField, TextField } from '@cartly/ui';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { ParamsEditor, normalizeParams, type ParamSource } from '../whatsapp/Campaigns';
import { slotsOf, slotTarget, type BuilderIssue, type Slot } from './graph';
import { NODE_INFO, summarize, TRIGGER_LABEL } from './labels';
import { templatesForPhone, type PhoneChoice, type TemplateChoice } from './useWhatsappChoices';

type FieldKind = 'text' | 'number' | 'bool' | 'consent' | 'payment' | 'risk' | 'cod';
const FIELDS: Record<
  string,
  {
    label: string;
    ops: { value: string; label: string }[];
    kind: FieldKind;
    orderOnly?: boolean;
  }
> = {
  tag: {
    label: 'Customer tag',
    ops: [
      { value: 'has', label: 'has' },
      { value: 'not_has', label: 'does not have' },
    ],
    kind: 'text',
  },
  ordersCount: {
    label: 'Number of orders',
    ops: [
      { value: 'gte', label: 'at least' },
      { value: 'lte', label: 'at most' },
      { value: 'eq', label: 'exactly' },
    ],
    kind: 'number',
  },
  totalSpent: {
    label: 'Total spent',
    ops: [
      { value: 'gte', label: 'at least' },
      { value: 'lte', label: 'at most' },
    ],
    kind: 'number',
  },
  lastOrder: {
    label: 'Last order',
    ops: [
      { value: 'within_days', label: 'within days' },
      { value: 'older_than_days', label: 'older than days' },
      { value: 'never', label: 'never ordered' },
    ],
    kind: 'number',
  },
  hasPhone: { label: 'Has phone number', ops: [{ value: 'is', label: 'is' }], kind: 'bool' },
  whatsappConsent: {
    label: 'WhatsApp consent',
    ops: [{ value: 'is', label: 'is' }],
    kind: 'consent',
  },
  'order.total': {
    label: 'This order’s total',
    ops: [
      { value: 'gte', label: 'at least' },
      { value: 'lte', label: 'at most' },
    ],
    kind: 'number',
    orderOnly: true,
  },
  'order.paymentMethod': {
    label: 'This order’s payment',
    ops: [{ value: 'is', label: 'is' }],
    kind: 'payment',
    orderOnly: true,
  },
  'order.riskLevel': {
    label: 'This order’s delivery risk',
    ops: [{ value: 'is', label: 'is' }],
    kind: 'risk',
    orderOnly: true,
  },
  'order.codStatus': {
    label: 'This order’s COD confirmation',
    ops: [{ value: 'is', label: 'is' }],
    kind: 'cod',
    orderOnly: true,
  },
};

function defaultCondition(field: string): FlowCondition {
  const f = FIELDS[field]!;
  const op = f.ops[0]!.value;
  const value =
    f.kind === 'text'
      ? 'vip'
      : f.kind === 'number'
        ? 1
        : f.kind === 'bool'
          ? true
          : f.kind === 'consent'
            ? 'opted_in'
            : f.kind === 'risk'
              ? 'high'
              : f.kind === 'cod'
                ? 'pending'
                : 'cod';
  return { field, op, value } as FlowCondition;
}

function ConditionRows({
  conditions,
  onChange,
  withOrder,
}: {
  conditions: FlowCondition[];
  onChange: (c: FlowCondition[]) => void;
  withOrder: boolean;
}) {
  const fieldOptions = Object.entries(FIELDS)
    .filter(([, f]) => withOrder || !f.orderOnly)
    .map(([value, f]) => ({ value, label: f.label }));
  const set = (i: number, c: FlowCondition) =>
    onChange(conditions.map((x, j) => (j === i ? c : x)));

  return (
    <div className="stack">
      {conditions.map((c, i) => {
        const f = FIELDS[c.field] ?? FIELDS['tag']!;
        const raw = 'value' in c && c.value !== undefined ? c.value : '';
        return (
          <div key={i} className="inspector__condition">
            <SelectField
              label="Check"
              value={c.field}
              onChange={(e) => set(i, defaultCondition(e.target.value))}
              options={fieldOptions}
            />
            <SelectField
              label="Is"
              value={c.op}
              onChange={(e) => {
                const op = e.target.value;
                if (c.field === 'lastOrder') {
                  set(
                    i,
                    (op === 'never'
                      ? { field: 'lastOrder', op }
                      : {
                          field: 'lastOrder',
                          op,
                          value: typeof raw === 'number' ? raw : 30,
                        }) as FlowCondition,
                  );
                } else set(i, { ...c, op } as FlowCondition);
              }}
              options={f.ops}
            />
            {c.op !== 'never' &&
              (f.kind === 'consent' ? (
                <SelectField
                  label="Value"
                  value={String(raw)}
                  onChange={(e) => set(i, { ...c, value: e.target.value } as FlowCondition)}
                  options={[
                    { value: 'opted_in', label: 'opted in' },
                    { value: 'opted_out', label: 'opted out' },
                    { value: 'unknown', label: 'no record' },
                  ]}
                />
              ) : f.kind === 'bool' ? (
                <SelectField
                  label="Value"
                  value={String(raw)}
                  onChange={(e) =>
                    set(i, { ...c, value: e.target.value === 'true' } as FlowCondition)
                  }
                  options={[
                    { value: 'true', label: 'yes' },
                    { value: 'false', label: 'no' },
                  ]}
                />
              ) : f.kind === 'risk' ? (
                <SelectField
                  label="Value"
                  value={String(raw)}
                  onChange={(e) => set(i, { ...c, value: e.target.value } as FlowCondition)}
                  options={[
                    { value: 'low', label: 'low' },
                    { value: 'medium', label: 'medium' },
                    { value: 'high', label: 'high' },
                  ]}
                />
              ) : f.kind === 'cod' ? (
                <SelectField
                  label="Value"
                  value={String(raw)}
                  onChange={(e) => set(i, { ...c, value: e.target.value } as FlowCondition)}
                  options={[
                    { value: 'none', label: 'not asked' },
                    { value: 'pending', label: 'waiting for a reply' },
                    { value: 'confirmed', label: 'confirmed' },
                    { value: 'declined', label: 'declined' },
                  ]}
                />
              ) : f.kind === 'payment' ? (
                <SelectField
                  label="Value"
                  value={String(raw)}
                  onChange={(e) => set(i, { ...c, value: e.target.value } as FlowCondition)}
                  options={[
                    { value: 'cod', label: 'cash on delivery' },
                    { value: 'prepaid', label: 'prepaid' },
                  ]}
                />
              ) : f.kind === 'number' ? (
                <TextField
                  label={c.field === 'lastOrder' ? 'Days' : 'Value'}
                  type="number"
                  min={0}
                  value={String(raw)}
                  onChange={(e) => set(i, { ...c, value: Number(e.target.value) } as FlowCondition)}
                />
              ) : (
                <TextField
                  label="Tag"
                  value={String(raw)}
                  maxLength={50}
                  onChange={(e) => set(i, { ...c, value: e.target.value } as FlowCondition)}
                />
              ))}
            {conditions.length > 1 && (
              <Button
                size="sm"
                variant="secondary"
                icon={<Trash2 size={14} />}
                onClick={() => onChange(conditions.filter((_, j) => j !== i))}
              >
                Remove condition
              </Button>
            )}
          </div>
        );
      })}
      {conditions.length < 10 && (
        <div>
          <Button
            size="sm"
            variant="secondary"
            icon={<Plus size={14} />}
            onClick={() => onChange([...conditions, defaultCondition('tag')])}
          >
            Add condition
          </Button>
        </div>
      )}
    </div>
  );
}

const SLOT_LABEL: Record<Slot, string> = {
  next: 'Then',
  yes: 'If yes, go to',
  no: 'If no, go to',
  a: 'Path A goes to',
  b: 'Path B goes to',
};

export function NodeInspector({
  node,
  graph,
  phones,
  templates,
  issues,
  editable,
  onChange,
  onLink,
  onSetStart,
  onDuplicate,
  onDelete,
}: {
  node: FlowNode;
  graph: FlowGraph;
  phones: PhoneChoice[];
  templates: TemplateChoice[];
  issues: BuilderIssue[];
  editable: boolean;
  onChange: (n: FlowNode) => void;
  onLink: (from: string, slot: Slot, to: string | null) => void;
  onSetStart: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const info = NODE_INFO[node.type];
  const withOrder = ORDER_TRIGGERS.includes(graph.trigger);
  const others = graph.nodes.filter((n) => n.id !== node.id);

  const phoneField = (n: Extract<FlowNode, { phoneNumberId: string }>) => (
    <SelectField
      label="Send from"
      value={n.phoneNumberId}
      onChange={(e) => {
        const phoneNumberId = e.target.value;
        if (n.type === 'send_whatsapp_template') {
          const list = templatesForPhone(phones, templates, phoneNumberId);
          const keep = list.find(
            (t) => t.name === n.templateName && t.language === n.templateLanguage,
          );
          const t = keep ?? list[0];
          onChange({
            ...n,
            phoneNumberId,
            templateName: t?.name ?? '',
            templateLanguage: t?.language ?? '',
            params: keep ? n.params : normalizeParams(t?.bodyParams ?? 0, []),
          } as FlowNode);
        } else onChange({ ...n, phoneNumberId });
      }}
      options={
        phones.some((p) => p.id === n.phoneNumberId)
          ? phones.map((p) => ({ value: p.id, label: p.label }))
          : [
              { value: n.phoneNumberId, label: 'Unknown number' },
              ...phones.map((p) => ({ value: p.id, label: p.label })),
            ]
      }
    />
  );

  let body: React.ReactNode;
  if (node.type === 'send_whatsapp_template') {
    const list = templatesForPhone(phones, templates, node.phoneNumberId);
    const key = `${node.templateName}|${node.templateLanguage}`;
    const current = list.find((t) => `${t.name}|${t.language}` === key);
    const options = list.map((t) => ({
      value: `${t.name}|${t.language}`,
      label: `${t.name} (${t.language})`,
    }));
    if (!current) {
      options.unshift({
        value: key,
        label: node.templateName ? `${node.templateName} (not approved)` : 'Choose a template',
      });
    }
    body = (
      <>
        {phoneField(node)}
        <SelectField
          label="Template"
          value={key}
          onChange={(e) => {
            const t = list.find((x) => `${x.name}|${x.language}` === e.target.value);
            if (t) {
              onChange({
                ...node,
                templateName: t.name,
                templateLanguage: t.language,
                params: normalizeParams(t.bodyParams, []) as unknown as typeof node.params,
              });
            }
          }}
          options={options}
        />
        {list.length === 0 && (
          <Alert tone="warning">
            This number has no approved templates yet. Sync templates on the WhatsApp page.
          </Alert>
        )}
        {current && (
          <ParamsEditor
            count={current.bodyParams}
            value={node.params as unknown as ParamSource[]}
            onChange={(v) => onChange({ ...node, params: v as unknown as typeof node.params })}
            withOrder={withOrder}
          />
        )}
        {withOrder && (
          <Checkbox
            label="Wait for the customer’s COD answer"
            description="Marks a COD order as “waiting for a reply”. A short “confirm”, “cancel” or “pay online” answer is then read and shown on the order."
            checked={node.awaitCodReply === true}
            onChange={(e) => onChange({ ...node, awaitCodReply: e.target.checked })}
          />
        )}
      </>
    );
  } else if (node.type === 'send_whatsapp_text') {
    body = (
      <>
        {phoneField(node)}
        <TextAreaField
          label="Message"
          value={node.text}
          maxLength={1024}
          rows={4}
          onChange={(e) => onChange({ ...node, text: e.target.value })}
        />
        {graph.trigger !== 'whatsapp.message_received' && (
          <Alert tone="warning">
            Free text can only follow a received WhatsApp message. Use a template, or start this
            flow from “WhatsApp message received”.
          </Alert>
        )}
      </>
    );
  } else if (node.type === 'wait') {
    body = (
      <div className="row">
        <TextField
          label="For"
          type="number"
          min={1}
          max={43200}
          value={String(node.amount)}
          onChange={(e) =>
            onChange({ ...node, amount: Math.max(1, Math.floor(Number(e.target.value))) })
          }
        />
        <SelectField
          label="Unit"
          value={node.unit}
          onChange={(e) => onChange({ ...node, unit: e.target.value as typeof node.unit })}
          options={[
            { value: 'minutes', label: 'minutes' },
            { value: 'hours', label: 'hours' },
            { value: 'days', label: 'days' },
          ]}
        />
      </div>
    );
  } else if (node.type === 'condition') {
    body = (
      <>
        <SelectField
          label="Continue on yes when"
          value={node.match}
          onChange={(e) => onChange({ ...node, match: e.target.value as 'all' | 'any' })}
          options={[
            { value: 'all', label: 'all conditions match' },
            { value: 'any', label: 'any condition matches' },
          ]}
        />
        <ConditionRows
          conditions={node.conditions}
          onChange={(conditions) => onChange({ ...node, conditions })}
          withOrder={withOrder}
        />
      </>
    );
  } else if (node.type === 'split') {
    body = (
      <>
        <TextField
          label="Share that takes path A (%)"
          type="number"
          min={1}
          max={99}
          value={String(node.percentA)}
          hint={`Path B gets the other ${100 - node.percentA}%. Each customer always gets the same path.`}
          onChange={(e) =>
            onChange({
              ...node,
              percentA: Math.min(99, Math.max(1, Math.floor(Number(e.target.value) || 1))),
            })
          }
        />
        <input
          type="range"
          min={1}
          max={99}
          value={node.percentA}
          aria-label="Share that takes path A"
          onChange={(e) => onChange({ ...node, percentA: Number(e.target.value) })}
        />
      </>
    );
  } else {
    body = (
      <TextField
        label="Tag"
        value={node.tag}
        maxLength={50}
        hint="Tags are lower-case; “VIP” and “vip” are the same tag."
        onChange={(e) => onChange({ ...node, tag: e.target.value })}
      />
    );
  }

  return (
    <div className="stack">
      <div>
        <h3 className="inspector__title">{info.title}</h3>
        <p className="text-muted text-sm">{info.help}</p>
      </div>
      {issues.length > 0 && (
        <Alert tone="critical" title="Fix this step">
          <ul className="issue-list">
            {issues.map((i, k) => (
              <li key={k}>{i.message}</li>
            ))}
          </ul>
        </Alert>
      )}
      <fieldset className="inspector__fields" disabled={!editable}>
        {body}
        {slotsOf(node).map((slot) => (
          <SelectField
            key={slot}
            label={SLOT_LABEL[slot]}
            value={slotTarget(node, slot) ?? ''}
            onChange={(e) => onLink(node.id, slot, e.target.value || null)}
            options={[
              { value: '', label: 'End of flow' },
              ...others.map((o) => ({
                value: o.id,
                label: `${NODE_INFO[o.type].title}: ${summarize(o).slice(0, 40)}`,
              })),
            ]}
          />
        ))}
      </fieldset>
      {editable && (
        <div className="row">
          {graph.start !== node.id && (
            <Button size="sm" variant="secondary" onClick={() => onSetStart(node.id)}>
              Make this the first step
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            icon={<Copy size={14} />}
            onClick={() => onDuplicate(node.id)}
          >
            Duplicate
          </Button>
          <Button
            size="sm"
            variant="danger"
            icon={<Trash2 size={14} />}
            onClick={() => onDelete(node.id)}
          >
            Delete step
          </Button>
        </div>
      )}
    </div>
  );
}

export function TriggerInspector({
  graph,
  editable,
  onChange,
}: {
  graph: FlowGraph;
  editable: boolean;
  onChange: (t: FlowTrigger) => void;
}) {
  return (
    <div className="stack">
      <div>
        <h3 className="inspector__title">Trigger</h3>
        <p className="text-muted text-sm">
          A run starts for a customer each time this happens, once per event. Orders older than 48
          hours never start a run.
        </p>
      </div>
      <fieldset className="inspector__fields" disabled={!editable}>
        <SelectField
          label="Start when"
          value={graph.trigger}
          onChange={(e) => onChange(e.target.value as FlowTrigger)}
          options={FLOW_TRIGGERS.map((t) => ({ value: t, label: TRIGGER_LABEL[t].title }))}
          hint={TRIGGER_LABEL[graph.trigger].when}
        />
      </fieldset>
      {(graph.trigger === 'checkout.abandoned' || graph.trigger === 'cart.abandoned') && (
        <Alert tone="info">
          Recovery stops by itself: if the customer completes the order or comes back to the{' '}
          {graph.trigger === 'cart.abandoned' ? 'cart' : 'checkout'} while a run is waiting, the
          remaining steps are skipped.
        </Alert>
      )}
    </div>
  );
}
